import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";

import SidebarLinkGroup from "./sidebar-link-group";
import LogoText from "../../assets/img/shiproneWorkflowText.svg";
import {
  RESET_SAVE_TEMPORARY_DATA,
  CLEAR_SEARCH,
  CHECK_IS_SIDEBAR_COLLAPSE,
} from "../../redux/constants";
import { useDispatch, useSelector } from "react-redux";

// Importing Routing Array
import { routes } from "../../constant/AuthenticationConstant";

function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  notificationList,
  requestCount,
  setRequestCount,
}) {
  const [notificatioCount, setNotificationCount] = useState();
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();

  const userData = useSelector((state) => state.auth);
  const role = userData.type;
  const sidebarRoutes = routes[role] ? routes[role] : [];

  const trigger = useRef(null);
  const sidebar = useRef(null);
  const dispatch = useDispatch();
  const { tempPageName } = useSelector((state) => state.tempWindowsData);
  const {logo, icon} = useSelector(state => state.palettes)

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? true : storedSidebarExpanded === "true"
  );
  const user = JSON.parse(localStorage.getItem("user"));
  const is_subscribed_inventory = user?.company?.is_subscribed_inventory;

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  useEffect(() => {
    dispatch({ type: CHECK_IS_SIDEBAR_COLLAPSE, payload: sidebarExpanded });
    localStorage.setItem("sidebar-expanded", sidebarExpanded);
    if (!sidebarExpanded) {
      document.querySelector("body").classList.add("sidebar-expanded");
    } else {
      document.querySelector("body").classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  useEffect(() => {
    let count = 0;
    notificationList.map((item) => (count = count + item.count));
    setNotificationCount(count);
  }, [notificationList]);

  return (
    <aside>
      {/* Sidebar backdrop (mobile only) */}
      <div
        className={`fixed inset-0 bg-slate-300 bg-opacity-30 z-40 lg:hidden lg:z-auto transition-opacity duration-200 ${
          sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      ></div>

      {/* Sidebar */}
      {/* #202E56 */}
      <div
        id="sidebar"
        ref={sidebar}
        className={`flex flex-col absolute z-40 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 h-screen overflow-y-scroll lg:overflow-y-auto no-scrollbar w-64 lg:w-20 lg:sidebar-expanded:!w-64 shrink-0 bg-[--bg-color-950] p-4 transition-all duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-64"
        }`}
      >
        {/* Sidebar header */}
        <div className="flex justify-between mb-10 pr-3 sm:px-2">
          {/* Close button */}
          <button
            ref={trigger}
            className="hidden text-slate-300 hover:text-slate-400"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <svg
              className="w-6 h-6 fill-current"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
            </svg>
          </button>
          {/* Logo for expanded screen*/}
          <div className={`${!sidebarExpanded ? "flex space-x-4" : "hidden"}`}>
            <div
              end
              to={"/overview"}
              className="block w-[36px]"
              onClick={() => setSidebarOpen(false)}
            >
              <img
                alt="logo"
                className=""
                src={icon ? icon : require("../../assets/img/Shiprone_forDark.png")}
              />
            </div>
            <div
              end
              to={"/overview"}
              className="block w-[155px] relative"
              onClick={() => setSidebarOpen(false)}
            >
              <img alt="text" src={logo ? logo : LogoText} />
            </div>
          </div>

          {/* Logo for collapse screen */}
          <div
            end
            to={"/overview"}
            className={`${sidebarExpanded ? "block" : "hidden"} w-[32px]`}
            onClick={() => setSidebarOpen(false)}
          >
            <img
              alt="logo"
              className="w-full h-full object-cover"
              src={ icon ? icon : require("../../assets/img/Shiprone_forDark.png") }
            />
          </div>
        </div>

        {/* Links */}
        <div className="space-y-8">
          {/* Pages group */}
          <div>
            {/* New Booking Button */}
            {(role === "super-admin" || role === "admin") && (
              <div
                className="mb-4 flex justify-center items-center px-3 py-2 !bg-[--bg-color-500] hover:!bg-[--bg-color-600] text-white border border-transparent rounded-[0.4rem] leading-5 shadow-sm transition duration-150 ease-in-out font-medium text-sm cursor-pointer"
                onClick={() => {
                  if (tempPageName === "bookings") {
                    dispatch({ type: RESET_SAVE_TEMPORARY_DATA });
                  }
                  dispatch({ type: CLEAR_SEARCH });
                  navigate(`/booking/create`);
                  setSidebarOpen(false);
                }}
              >
                <span
                  className={`fas fa-plus ${
                    !sidebarExpanded || sidebarOpen ? "mr-2" : "mr-0"
                  }`}
                ></span>
                <p
                  className={`${
                    !sidebarExpanded || sidebarOpen ? "block" : "hidden"
                  } text-sm leading-5`}
                >
                  New Booking
                </p>
              </div>
            )}

            {is_subscribed_inventory ? (
              <ul className="mt-5">
                {sidebarRoutes.length > 0 &&
                  sidebarRoutes.map((item, index) => {
                    return item.label === "Overview" ? (
                      <SidebarLinkGroup active={pathname === "/"}>
                        {(active) => (
                          <Tippy
                            placement="right"
                            arrow={false}
                            content={item.label}
                            transitionFlip={false}
                            size="small"
                            className={`${!sidebarExpanded ? "hidden" : ""}`}
                          >
                            <NavLink
                              end
                              to={item.path}
                              className={`flex items-center ${
                                !sidebarOpen && sidebarExpanded
                                  ? "w-full justify-center"
                                  : ""
                              } px-3 py-2 transition duration-150 truncate ${
                                active ? "text-white" : "text-slate-300"
                              }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <span
                                className={`${item.icon} fill-current text-xl ${
                                  active ? "text-white" : "text-slate-300"
                                }`}
                              ></span>
                              <span
                                className={`${
                                  sidebarOpen || !sidebarExpanded
                                    ? "block"
                                    : "hidden"
                                } text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 duration-200`}
                              >
                                {item.label}
                              </span>
                            </NavLink>
                          </Tippy>
                        )}
                      </SidebarLinkGroup>
                    ) : (
                      <>
                        {index <= 6 && (
                          <SidebarLinkGroup
                            active={pathname.includes(item.path)}
                          >
                            {(active) => (
                              <Tippy
                                placement="right"
                                arrow={false}
                                content={item.label}
                                transitionFlip={false}
                                size="small"
                                className={`${
                                  !sidebarExpanded ? "hidden" : ""
                                }`}
                              >
                                <NavLink
                                  end
                                  to={item.path}
                                  className={`flex items-center ${
                                    !sidebarOpen && sidebarExpanded
                                      ? "w-full justify-center"
                                      : ""
                                  } px-3 py-2 transition duration-150 truncate ${
                                    active ? "text-white" : "text-slate-300"
                                  }`}
                                  onClick={() => setSidebarOpen(false)}
                                >
                                  <span
                                    className={`${
                                      item.icon
                                    } fill-current text-xl ${
                                      item.label === "Requests" && "relative"
                                    } ${
                                      active ? "text-white" : "text-slate-300"
                                    }`}
                                  >
                                    {item.label === "Requests" &&
                                      requestCount !== 0 &&
                                      sidebarExpanded &&
                                      !sidebarOpen && (
                                        <div className="absolute top-0 -right-1 w-2.5 h-2.5 bg-rose-500 rounded-full text-[10px]"></div>
                                      )}
                                  </span>
                                  <span
                                    className={`${
                                      sidebarOpen || !sidebarExpanded
                                        ? "block"
                                        : "hidden"
                                    } text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 duration-200`}
                                  >
                                    {item.label}
                                  </span>
                                  {item.label === "Requests" &&
                                    requestCount !== 0 &&
                                    (sidebarOpen || !sidebarExpanded) && (
                                      <span
                                        className={`ml-auto px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                          !active
                                            ? "bg-white/10"
                                            : "border border-white/10 bg-white/20"
                                        }`}
                                      >
                                        {requestCount}
                                      </span>
                                    )}
                                </NavLink>
                              </Tippy>
                            )}
                          </SidebarLinkGroup>
                        )}

                        {index === 6 && (
                          <>
                            <div
                              className={`px-3 py-4 w-full flex justify-center items-center text-xs leading-5 font-thin tracking-wider text-slate-300 cursor-pointer`}
                              onClick={() =>
                                setShowMoreOptions(!showMoreOptions)
                              }
                            >
                              {showMoreOptions ? "Less" : "More"}
                            </div>
                          </>
                        )}

                        {index > 6 && showMoreOptions && (
                          <SidebarLinkGroup
                            active={pathname.includes(item.path)}
                          >
                            {(active) => (
                              <Tippy
                                placement="right"
                                arrow={false}
                                content={item.label}
                                transitionFlip={false}
                                size="small"
                                className={`${
                                  !sidebarExpanded ? "hidden" : ""
                                }`}
                              >
                                <NavLink
                                  end
                                  to={item.path}
                                  className={`flex items-center ${
                                    !sidebarOpen && sidebarExpanded
                                      ? "w-full justify-center"
                                      : ""
                                  } px-3 py-2 transition duration-150 truncate ${
                                    active ? "text-white" : "text-slate-300"
                                  }`}
                                  onClick={() => setSidebarOpen(false)}
                                >
                                  <span
                                    className={`${
                                      item.icon
                                    } fill-current text-xl ${
                                      item.label === "Requests" && "relative"
                                    } ${
                                      active ? "text-white" : "text-slate-300"
                                    }`}
                                  >
                                    {item.label === "Requests" &&
                                      requestCount !== 0 &&
                                      sidebarExpanded &&
                                      !sidebarOpen && (
                                        <div className="absolute top-0 -right-1 w-2.5 h-2.5 bg-rose-500 rounded-full text-[10px]"></div>
                                      )}
                                  </span>
                                  <span
                                    className={`${
                                      sidebarOpen || !sidebarExpanded
                                        ? "block"
                                        : "hidden"
                                    } text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 duration-200`}
                                  >
                                    {item.label}
                                  </span>
                                  {item.label === "Requests" &&
                                    requestCount !== 0 &&
                                    (sidebarOpen || !sidebarExpanded) && (
                                      <span
                                        className={`ml-auto px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                          !active
                                            ? "bg-white/10"
                                            : "border border-white/10 bg-white/20"
                                        }`}
                                      >
                                        {requestCount}
                                      </span>
                                    )}
                                </NavLink>
                              </Tippy>
                            )}
                          </SidebarLinkGroup>
                        )}
                      </>
                    );
                  })}
              </ul>
            ) : (
              <ul className="mt-5">
                {sidebarRoutes.length > 0 &&
                  sidebarRoutes
                    .filter(
                      (ele) =>
                        ele.label !== "Manage Orders" &&
                        ele.label !== "Manage Inventory" &&
                        ele.label !== "Manage SKUs" &&
                        ele.label !== "Manage Products"
                    )
                    .map((item, index) => {
                      return item.label === "Overview" ? (
                        <SidebarLinkGroup active={pathname === "/"}>
                          {(active) => (
                            <Tippy
                              placement="right"
                              arrow={false}
                              content={item.label}
                              transitionFlip={false}
                              size="small"
                              className={`${!sidebarExpanded ? "hidden" : ""}`}
                            >
                              <NavLink
                                end
                                to={item.path}
                                className={`flex items-center ${
                                  !sidebarOpen && sidebarExpanded
                                    ? "w-full justify-center"
                                    : ""
                                } px-3 py-2 transition duration-150 truncate ${
                                  active ? "text-white" : "text-slate-300"
                                }`}
                                onClick={() => setSidebarOpen(false)}
                              >
                                <span
                                  className={`${
                                    item.icon
                                  } fill-current text-xl ${
                                    active ? "text-white" : "text-slate-300"
                                  }`}
                                ></span>
                                <span
                                  className={`${
                                    sidebarOpen || !sidebarExpanded
                                      ? "block"
                                      : "hidden"
                                  } text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 duration-200`}
                                >
                                  {item.label}
                                </span>
                              </NavLink>
                            </Tippy>
                          )}
                        </SidebarLinkGroup>
                      ) : (
                        <>
                          {index <= 4 && (
                            <SidebarLinkGroup
                              active={pathname.includes(item.path)}
                            >
                              {(active) => (
                                <Tippy
                                  placement="right"
                                  arrow={false}
                                  content={item.label}
                                  transitionFlip={false}
                                  size="small"
                                  className={`${
                                    !sidebarExpanded ? "hidden" : ""
                                  }`}
                                >
                                  <NavLink
                                    end
                                    to={item.path}
                                    className={`flex items-center ${
                                      !sidebarOpen && sidebarExpanded
                                        ? "w-full justify-center"
                                        : ""
                                    } px-3 py-2 transition duration-150 truncate ${
                                      active ? "text-white" : "text-slate-300"
                                    }`}
                                    onClick={() => setSidebarOpen(false)}
                                  >
                                    <span
                                      className={`${
                                        item.icon
                                      } fill-current text-xl ${
                                        item.label === "Requests" && "relative"
                                      } ${
                                        active ? "text-white" : "text-slate-300"
                                      }`}
                                    >
                                      {item.label === "Requests" &&
                                        requestCount !== 0 &&
                                        sidebarExpanded &&
                                        !sidebarOpen && (
                                          <div className="absolute top-0 -right-1 w-2.5 h-2.5 bg-rose-500 rounded-full text-[10px]"></div>
                                        )}
                                    </span>
                                    <span
                                      className={`${
                                        sidebarOpen || !sidebarExpanded
                                          ? "block"
                                          : "hidden"
                                      } text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 duration-200`}
                                    >
                                      {item.label}
                                    </span>
                                    {item.label === "Requests" &&
                                      requestCount !== 0 &&
                                      (sidebarOpen || !sidebarExpanded) && (
                                        <span
                                          className={`ml-auto px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                            !active
                                              ? "bg-white/10"
                                              : "border border-white/10 bg-white/20"
                                          }`}
                                        >
                                          {requestCount}
                                        </span>
                                      )}
                                  </NavLink>
                                </Tippy>
                              )}
                            </SidebarLinkGroup>
                          )}

                          {index === 4 && (
                            <>
                              <div
                                className={`px-3 py-4 w-full flex justify-center items-center text-xs leading-5 font-thin tracking-wider text-slate-300 cursor-pointer`}
                                onClick={() =>
                                  setShowMoreOptions(!showMoreOptions)
                                }
                              >
                                {showMoreOptions ? "Less" : "More"}
                              </div>
                            </>
                          )}

                          {index > 4 && showMoreOptions && (
                            <SidebarLinkGroup
                              active={pathname.includes(item.path)}
                            >
                              {(active) => (
                                <Tippy
                                  placement="right"
                                  arrow={false}
                                  content={item.label}
                                  transitionFlip={false}
                                  size="small"
                                  className={`${
                                    !sidebarExpanded ? "hidden" : ""
                                  }`}
                                >
                                  <NavLink
                                    end
                                    to={item.path}
                                    className={`flex items-center ${
                                      !sidebarOpen && sidebarExpanded
                                        ? "w-full justify-center"
                                        : ""
                                    } px-3 py-2 transition duration-150 truncate ${
                                      active ? "text-white" : "text-slate-300"
                                    }`}
                                    onClick={() => setSidebarOpen(false)}
                                  >
                                    <span
                                      className={`${
                                        item.icon
                                      } fill-current text-xl ${
                                        item.label === "Requests" && "relative"
                                      } ${
                                        active ? "text-white" : "text-slate-300"
                                      }`}
                                    >
                                      {item.label === "Requests" &&
                                        requestCount !== 0 &&
                                        sidebarExpanded &&
                                        !sidebarOpen && (
                                          <div className="absolute top-0 -right-1 w-2.5 h-2.5 bg-rose-500 rounded-full text-[10px]"></div>
                                        )}
                                    </span>
                                    <span
                                      className={`${
                                        sidebarOpen || !sidebarExpanded
                                          ? "block"
                                          : "hidden"
                                      } text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 duration-200`}
                                    >
                                      {item.label}
                                    </span>
                                    {item.label === "Requests" &&
                                      requestCount !== 0 &&
                                      (sidebarOpen || !sidebarExpanded) && (
                                        <span
                                          className={`ml-auto px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                            !active
                                              ? "bg-white/10"
                                              : "border border-white/10 bg-white/20"
                                          }`}
                                        >
                                          {requestCount}
                                        </span>
                                      )}
                                  </NavLink>
                                </Tippy>
                              )}
                            </SidebarLinkGroup>
                          )}
                        </>
                      );
                    })}
              </ul>
            )}
          </div>
        </div>

        {/* Expand / collapse button */}
        <div className="pt-3 hidden lg:inline-flex justify-end mt-auto">
          <div className="px-3 py-2">
            <button onClick={() => setSidebarExpanded(!sidebarExpanded)}>
              <span className="sr-only">Expand / collapse sidebar</span>
              <svg
                className="w-6 h-6 fill-current sidebar-expanded:rotate-180"
                viewBox="0 0 24 24"
              >
                <path
                  className="text-slate-300"
                  d="M19.586 11l-5-5L16 4.586 23.414 12 16 19.414 14.586 18l5-5H7v-2z"
                />
                <path className="text-slate-300" d="M3 23H1V1h2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
